/**
 * StatementDetailsDialog.js
 * Dialog component for displaying detailed statement information
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Avatar,
  Grid,
  LinearProgress,
  Divider,
  Alert,
  alpha,
  useTheme,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { getReconciliationProgress, formatDate } from './statementUtils';

const StatementDetailsDialog = ({ open, onClose, statement, onStartReconciliation }) => {
  const theme = useTheme();

  if (!statement) return null;

  const progress = getReconciliationProgress(statement);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalhes do Extrato</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Header with File Info */}
          <Box sx={{ textAlign: 'center', pb: 2 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              }}
            >
              <FileIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" fontWeight="700" gutterBottom>
              {statement.fileName}
            </Typography>
            {/* Status Chip */}
            <Box sx={{ display: 'inline-block' }}>
              {progress === 100 ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  ✓ Concluído
                </Box>
              ) : progress > 0 ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  ⟳ {progress.toFixed(0)}%
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.grey[500]} 0%, ${theme.palette.grey[700]} 100%)`,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  ○ Pendente
                </Box>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Basic Information */}
          <Box>
            <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="600">
              Informações Básicas
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Conta Bancária
                </Typography>
                <Typography variant="body1">
                  {statement.bankAccount?.name || 'Não identificada'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Período do Extrato
                </Typography>
                <Typography variant="body1">
                  {formatDate(statement.periodStart || statement.statementStartDate)} -{' '}
                  {formatDate(statement.periodEnd || statement.statementEndDate)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Data de Upload
                </Typography>
                <Typography variant="body1">
                  {formatDate(statement.createdAt || statement.dateCreate || statement.uploadDate)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Transaction Statistics */}
          <Box>
            <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="600">
              Estatísticas das Transações
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                  }}
                >
                  <Typography variant="h4" fontWeight="700" color="info.main">
                    {statement.totalTransactions || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                  }}
                >
                  <Typography variant="h4" fontWeight="700" color="success.main">
                    {statement.reconciledTransactions || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Conciliadas
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                  }}
                >
                  <Typography variant="h4" fontWeight="700" color="warning.main">
                    {(statement.totalTransactions || 0) - (statement.reconciledTransactions || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pendentes
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Progress Bar */}
            <Box sx={{ mt: 3 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Progresso da Conciliação
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  {progress.toFixed(0)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Processing Notes */}
          {statement.processingNotes && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="600">
                  Observações do Processamento
                </Typography>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                  >
                    {statement.processingNotes}
                  </Typography>
                </Alert>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
        <Button
          onClick={() => {
            onStartReconciliation(statement);
            onClose();
          }}
          variant="contained"
          color="primary"
          startIcon={<StartIcon />}
          sx={{ minWidth: 160 }}
        >
          Iniciar Conciliação
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatementDetailsDialog;
